package com.evoapps.evoshift.data.mapper

import com.evoapps.evoshift.data.dto.finance.DailyCostsDto
import com.evoapps.evoshift.data.dto.finance.DailyEfficiencyDto
import com.evoapps.evoshift.data.dto.finance.DailyLaborStatsDto
import com.evoapps.evoshift.data.dto.finance.DailyRecordDto
import com.evoapps.evoshift.data.dto.finance.DailyRevenueDto
import com.evoapps.evoshift.data.dto.finance.LaborSummaryDto
import com.evoapps.evoshift.domain.model.DailyCosts
import com.evoapps.evoshift.domain.model.DailyEfficiency
import com.evoapps.evoshift.domain.model.DailyLaborStats
import com.evoapps.evoshift.domain.model.DailyRecord
import com.evoapps.evoshift.domain.model.DailyRevenue
import com.evoapps.evoshift.domain.model.LaborSummary
import com.evoapps.evoshift.domain.model.RevenueSource
import com.evoapps.evoshift.domain.model.SystemContractType

class DailyRecordMapper {

    fun toDomain(dto: DailyRecordDto): DailyRecord {
        return DailyRecord(
            id = dto.id,
            rosterId = dto.rosterId,
            workplaceId = dto.workplaceId,
            date = dto.date,
            revenue = DailyRevenue(
                planNetto = dto.revenue.planNetto,
                realBrutto = dto.revenue.realBrutto,
                realNetto = dto.revenue.realNetto,
                previousYearBrutto = dto.revenue.previousYearBrutto,
                previousYearNetto = dto.revenue.previousYearNetto,
                fixedRevenue = dto.revenue.fixedRevenue,
                commissionPercent = dto.revenue.commissionPercent,
                comments = dto.revenue.comments,
                otherRevenue = dto.revenue.otherRevenue,
                source = RevenueSource.fromId(dto.revenue.source),
                importedAt = dto.revenue.importedAt
            ),
            laborStats = DailyLaborStats(
                planned = LaborSummary(
                    totalCost = dto.laborStats.planned.totalCost,
                    totalHours = dto.laborStats.planned.totalHours,
                    laborPercentage = dto.laborStats.planned.laborPercentage
                ),
                actual = LaborSummary(
                    totalCost = dto.laborStats.actual.totalCost,
                    totalHours = dto.laborStats.actual.totalHours,
                    laborPercentage = dto.laborStats.actual.laborPercentage
                )
            ),
            costs = DailyCosts(
                plannedHours = dto.costs.plannedHours,
                actualHours = dto.costs.actualHours,
                plannedByContractType = dto.costs.plannedByContractType.mapKeys { 
                    SystemContractType.valueOf(it.key) 
                },
                actualByContractType = dto.costs.actualByContractType.mapKeys { 
                    SystemContractType.valueOf(it.key) 
                },
                otherCosts = dto.costs.otherCosts,
                openShifts = dto.costs.openShifts,
                openHours = dto.costs.openHours,
                openCost = dto.costs.openCost,
                openCostPerHour = dto.costs.openCostPerHour,
                openRateSource = dto.costs.openRateSource
            ),
            efficiency = DailyEfficiency(
                spmhReal = dto.efficiency.spmhReal
            ),
            isLocked = dto.isLocked
        )
    }

    fun toDto(domain: DailyRecord): DailyRecordDto {
        return DailyRecordDto(
            id = domain.id,
            rosterId = domain.rosterId,
            workplaceId = domain.workplaceId,
            date = domain.date,
            revenue = DailyRevenueDto(
                planNetto = domain.revenue.planNetto,
                realBrutto = domain.revenue.realBrutto,
                realNetto = domain.revenue.realNetto,
                previousYearBrutto = domain.revenue.previousYearBrutto,
                previousYearNetto = domain.revenue.previousYearNetto,
                fixedRevenue = domain.revenue.fixedRevenue,
                commissionPercent = domain.revenue.commissionPercent,
                comments = domain.revenue.comments,
                otherRevenue = domain.revenue.otherRevenue,
                source = domain.revenue.source?.name,
                importedAt = domain.revenue.importedAt
            ),
            laborStats = DailyLaborStatsDto(
                planned = LaborSummaryDto(
                    totalCost = domain.laborStats.planned.totalCost,
                    totalHours = domain.laborStats.planned.totalHours,
                    laborPercentage = domain.laborStats.planned.laborPercentage
                ),
                actual = LaborSummaryDto(
                    totalCost = domain.laborStats.actual.totalCost,
                    totalHours = domain.laborStats.actual.totalHours,
                    laborPercentage = domain.laborStats.actual.laborPercentage
                )
            ),
            costs = DailyCostsDto(
                plannedHours = domain.costs.plannedHours,
                actualHours = domain.costs.actualHours,
                plannedByContractType = domain.costs.plannedByContractType.mapKeys { it.key.name },
                actualByContractType = domain.costs.actualByContractType.mapKeys { it.key.name },
                otherCosts = domain.costs.otherCosts,
                openShifts = domain.costs.openShifts,
                openHours = domain.costs.openHours,
                openCost = domain.costs.openCost,
                openCostPerHour = domain.costs.openCostPerHour,
                openRateSource = domain.costs.openRateSource
            ),
            efficiency = DailyEfficiencyDto(
                spmhReal = domain.efficiency.spmhReal
            ),
            isLocked = domain.isLocked
        )
    }
}
